/**
 * Founder Dashboard Layout
 *
 * Main layout with tab navigation for the founder dashboard.
 * Provides consistent header and nav across all dashboard pages.
 */

import { NavLink, Outlet, Navigate, useLocation } from 'react-router-dom';
import {
  ChartBarIcon,
  HeartIcon,
  BuildingOfficeIcon,
  ArrowTrendingUpIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline';

const navItems = [
  { path: 'summary', label: 'Summary', icon: ChartBarIcon },
  { path: 'engagement', label: 'Engagement', icon: HeartIcon },
  { path: 'organizations', label: 'Organizations', icon: BuildingOfficeIcon },
  { path: 'growth', label: 'Growth', icon: ArrowTrendingUpIcon },
  { path: 'retention', label: 'Retention', icon: UserGroupIcon },
  { path: 'revenue', label: 'Revenue', icon: CurrencyDollarIcon },
  { path: 'infrastructure', label: 'Infrastructure', icon: ServerStackIcon },
];

export default function FounderDashboardLayout() {
  const location = useLocation();

  // Redirect /founder-dashboard to /founder-dashboard/summary
  if (location.pathname === '/founder-dashboard') {
    return <Navigate to="/founder-dashboard/summary" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-deep-navy to-storm-blue text-white px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl font-bold">Founder Dashboard</h1>
          <p className="text-blue-100 text-sm mt-1">
            Platform analytics and business intelligence
          </p>
        </div>
      </header>

      {/* Tab Navigation */}
      <nav className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex overflow-x-auto scrollbar-hide">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={`/founder-dashboard/${item.path}`}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    isActive
                      ? 'border-blue-600 text-blue-600 bg-blue-50'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`
                }
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-7xl mx-auto p-6">
        <Outlet />
      </main>
    </div>
  );
}
